/* Copyright (c) 2018-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { HttpClient, HttpResponse, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, mergeMap, timeout, retryWhen } from 'rxjs/operators';
import { Observable, throwError, timer, Subject } from 'rxjs';
import { extend, isEqual, isNil } from 'lodash';
import { NetworkStatusCheckService } from 'utils/lib/network-status-check/network-status-check.service';
import { generateUuid } from 'utils/lib/common/common-utils';
import { TimeoutErrorName, StatusCodeZeroErrorTypes, NvHttpEndpointDefaultRetryConfig } from '../constants/constants';

export const HeaderType = {
    authorization: 'Authorization',
    xRequestId: 'x-request-id'
};

export interface PropertyBag {
    [header: string]: string | string[];
}

export interface NvReportRequestData {
    [header: string]: string;
}

export interface RetryConfig {
    defaultTimeout?: number;
    defaultTimeBetweenRetries?: number;
    defaultRetries: number;
    retryOnTimeout?: boolean;
    exponentialBackoffMaxDelay?: number;
    exponentialBackoffFirstRetryInterval?: number;
    exponentGrowthFactor?: number;
}

export class NetworkHttpEndpointRetryConfig implements RetryConfig {
    defaultTimeout?: number;
    // If defaultTimeBetweenRetries is set, it will use the constant delay instead of the exponential backoff.
    defaultTimeBetweenRetries?: number;
    defaultRetries: number;
    retryOnTimeout?: boolean;
    exponentialBackoffMaxDelay?: number;
    exponentialBackoffFirstRetryInterval?: number;
    exponentGrowthFactor?: number;
}

export const HTTP_ERROR_SERVER_RETRY = 429;
export const HTTP_ERROR_SERVICE_UNAVAILABLE = 503;

// RetryConfig, includeRequestId, and reportRequest are the only additions to the base HttpClient 'options'
// this interface does not currently support 'HttpParams' but is open
// to extension if required
export interface NvRequestOptions {
    body?: any;
    headers?: HttpHeaders | PropertyBag;
    reportProgress?: boolean;
    params?: PropertyBag;
    responseType?: 'json' | 'text' | 'blob' | 'arraybuffer';
    withCredentials?: boolean;
    config?: RetryConfig;
    includeRequestId?: boolean;
    reportRequest?: boolean;
    reportRequestData?: NvReportRequestData;
    includeFullResponse?: boolean;
    // Pass retryConfigUpdated$ to update the retry config dynamically
    retryConfigUpdated$?: Observable<RetryConfig>;
}

export interface NVErrorResponse {
    url: string;
    status: number;
    xRequestId?: string;
    rawResponse?: HttpErrorResponse;
}

export interface NvRequestEvent {
    url: string;
    method: string;
    options: NvRequestOptions;
}

export interface NvErrorResponseEvent {
    response: NVErrorResponse;
    options: NvRequestOptions;
}

export interface NvSuccessResponseEvent {
    response: HttpResponse<any>;
    options: NvRequestOptions;
}

export class NvHttpEndpoint {
    private static httpErrorEventsSubject: Subject<NvErrorResponseEvent> = new Subject<NvErrorResponseEvent>();
    private static httpSuccessEventsSubject: Subject<NvSuccessResponseEvent> = new Subject<NvSuccessResponseEvent>();
    private static httpStartEventsSubject: Subject<NvRequestEvent> = new Subject<NvRequestEvent>();

    static httpErrorEvents$() {
        return NvHttpEndpoint.httpErrorEventsSubject.asObservable();
    }
    static httpSuccessEvents$() {
        return NvHttpEndpoint.httpSuccessEventsSubject.asObservable();
    }
    static httpStartEvents$() {
        return NvHttpEndpoint.httpStartEventsSubject.asObservable();
    }

    constructor(
        private http: HttpClient,
        private networkStatusService: NetworkStatusCheckService,
        private configOptions: NvRequestOptions = {},
        private networkHttpEndpointRetryConfig: NetworkHttpEndpointRetryConfig = NvHttpEndpointDefaultRetryConfig
    ) {
        if (isNil(this.configOptions.config)) {
            this.configOptions.config = this.networkHttpEndpointRetryConfig;
        }
        if (isNil(this.configOptions.config.retryOnTimeout)) {
            this.configOptions.config.retryOnTimeout = this.networkHttpEndpointRetryConfig.retryOnTimeout;
        }
        if (isNil(this.configOptions.config.exponentialBackoffMaxDelay)) {
            this.configOptions.config.exponentialBackoffMaxDelay =
                this.networkHttpEndpointRetryConfig.exponentialBackoffMaxDelay;
        }
        if (isNil(this.configOptions.config.exponentialBackoffFirstRetryInterval)) {
            this.configOptions.config.exponentialBackoffFirstRetryInterval =
                this.networkHttpEndpointRetryConfig.exponentialBackoffFirstRetryInterval;
        }
        if (isNil(this.configOptions.config.exponentGrowthFactor)) {
            this.configOptions.config.exponentGrowthFactor = this.networkHttpEndpointRetryConfig.exponentGrowthFactor;
        }

        if (!isNil(this.configOptions.retryConfigUpdated$)) {
            this.configOptions.retryConfigUpdated$.subscribe(retryConfig => {
                if (isNil(retryConfig) || isEqual(this.configOptions.config, retryConfig)) {
                    return;
                }
                this.configOptions.config = { ...this.configOptions.config, ...retryConfig };
            });
        }
    }

    static replaceTemplateKey(
        currentUrl: string,
        key: string,
        replaceValue: string | string[],
        deleteFn: (key: string) => void
    ): string {
        let newUrl: string = currentUrl;
        if (typeof replaceValue === 'string') {
            const searchValue = ':' + key;
            if (currentUrl.indexOf(searchValue) !== -1) {
                newUrl = currentUrl.replace(new RegExp(searchValue, 'g'), encodeURIComponent(replaceValue));
                deleteFn(key);
            }
        }
        return newUrl;
    }

    static formatUrl(url: string, params: PropertyBag): string {
        let formattedUrl = url;
        formattedUrl = Object.keys(params).reduce(
            (prev, cur) =>
                NvHttpEndpoint.replaceTemplateKey(prev, cur, params[cur], deleteKey => delete params[deleteKey]),
            formattedUrl
        );
        return formattedUrl;
    }

    static isRetryableError(
        error: Error,
        config: RetryConfig,
        networkStatusService: NetworkStatusCheckService
    ): boolean {
        let shouldRetry = true;
        if (error instanceof HttpErrorResponse) {
            // The backend returned an unsuccessful response code.
            if (error.status === 0 && !networkStatusService.isOnline) {
                shouldRetry = false;
            } else if (error.status === HTTP_ERROR_SERVER_RETRY) {
                shouldRetry = true;
            } else if (error.status !== 0 && (error.status < 500 || error.status >= 600)) {
                shouldRetry = false;
            }
        } else if (error instanceof Error) {
            if (error.name === TimeoutErrorName && !config.retryOnTimeout) {
                shouldRetry = false;
            }
        }
        return shouldRetry;
    }

    /**
     *
     * @param url - base url used for the request
     * @param params - query params
     * @param error - Error thrown - either timeout or HttpErrorResponse
     *
     * This method takes the given error object of type Error or HttpErrorResponse and wraps it in
     * a new class NVErrorResponse. This is done for following reason -
     *
     * failure to connect to the server due to network being disconnected results
     * in an HttpErrorResponse with url = null. Basically we get something like
     * {
     *      'name': 'HTTPErrorResponse',
     *      'status': 0,
     *      'url': null,
     *      .
     *      .
     *      .
     * }
     * For telemetry purpose, we need the url to report the HTTPFailure event. The url field in HTTPErrorResponse is read-only.
     * Therefore, we need to capture the url in a new field and therefore, we wrap the HTTPErrorResponse in
     * the NVErrorResponse object.
     *
     * For timeout where the error is thrown by the timeout operator, we dont have HTTPErrorResponse and therefore,
     * we add our custom status code for timeout and the url in NVErrorResponse.
     */
    static formatError(url: string, params: PropertyBag, error: Error, options: NvRequestOptions): NVErrorResponse {
        const errorUrl = NvHttpEndpoint.getFullUrl(url, params);
        let rawResponse: HttpErrorResponse;
        let status: number;
        if (error instanceof HttpErrorResponse) {
            rawResponse = error;
            status = error.status;
        } else if (error && error.name === TimeoutErrorName) {
            status = StatusCodeZeroErrorTypes.RequestTimeout;
        }

        const errorResponse: NVErrorResponse = {
            url: errorUrl,
            status,
            rawResponse
        };

        if (!isNil(options.headers[HeaderType.xRequestId])) {
            errorResponse.xRequestId = options.headers[HeaderType.xRequestId];
        }

        return errorResponse;
    }

    static getFullUrl(url: string, params: PropertyBag): string {
        const keys = Object.keys(params);
        if (keys.length > 0) {
            url = url + '?';
            for (const key of keys) {
                const value = params[key];
                url += `${key}=${value}&`;
            }
            url = url.substring(0, url.length - 1);
        }
        return url;
    }

    static applyTimeout<T>(httpRequest: Observable<HttpResponse<T>>, config: RetryConfig): Observable<HttpResponse<T>> {
        if (!isNil(config.defaultTimeout)) {
            return httpRequest.pipe(timeout(config.defaultTimeout));
        }

        return httpRequest;
    }

    static isReportRequest(options: NvRequestOptions): boolean {
        return isNil(options.reportRequest) || options.reportRequest === true;
    }

    private genericRetryStrategy(url: string, options: NvRequestOptions, attempts: Observable<any>) {
        return attempts.pipe(
            mergeMap((error, i) => {
                const numOfAttempts = i + 1;
                const errorResponse: NVErrorResponse = NvHttpEndpoint.formatError(url, options.params, error, options);
                if (NvHttpEndpoint.isReportRequest(options)) {
                    options.reportRequestData = { ...options.reportRequestData, retryCount: String(numOfAttempts) };
                    NvHttpEndpoint.httpErrorEventsSubject.next({ response: errorResponse, options });
                }

                // if maximum number of retries have been met
                // or response is a status code we don't wish to retry, throw error
                if (
                    numOfAttempts > options.config.defaultRetries ||
                    !NvHttpEndpoint.isRetryableError(error, options.config, this.networkStatusService)
                ) {
                    return throwError(errorResponse);
                }

                let retryTime;
                if (options.config.defaultTimeBetweenRetries) {
                    retryTime = options.config.defaultTimeBetweenRetries;
                } else {
                    // apply exponential backoff
                    const maxDelay = options.config?.exponentialBackoffMaxDelay;
                    const firstRetryInterval = options.config?.exponentialBackoffFirstRetryInterval;
                    const exponentGrowthFactor = options.config?.exponentGrowthFactor;
                    retryTime = Math.min(
                        Math.pow(2, exponentGrowthFactor * (numOfAttempts - 1)) * firstRetryInterval,
                        maxDelay
                    );
                }

                if (
                    error instanceof HttpErrorResponse &&
                    (error.status === HTTP_ERROR_SERVER_RETRY || error.status === HTTP_ERROR_SERVICE_UNAVAILABLE)
                ) {
                    const retryAfter = +(error.headers.get('retry-after') || 0);
                    if (!isNaN(retryAfter)) {
                        retryTime = Math.max(retryTime, retryAfter * 1000);
                    }
                }
                return timer(retryTime);
            })
        );
    }

    private initRequest(url: string, clientOptions: NvRequestOptions): [NvRequestOptions, string] {
        const options = clientOptions;
        options.params = { ...this.configOptions.params, ...clientOptions.params };
        options.headers = { ...this.configOptions.headers, ...clientOptions.headers };
        options.config = { ...this.configOptions.config, ...clientOptions.config };
        options.reportRequestData = { ...this.configOptions.reportRequestData, ...clientOptions.reportRequestData };
        options.reportRequest = this.configOptions.reportRequest && clientOptions.reportRequest;
        if (options.includeRequestId) {
            options.headers[HeaderType.xRequestId] = generateUuid();
        }
        const formattedUrl = NvHttpEndpoint.formatUrl(url, options.params);
        return [options, formattedUrl];
    }

    // allows client to hit a url with template params and query params
    // www.server.com/:version/serverInfo?foo=bar
    // the 'static' parts can be passed into the constructor and
    // the 'dynamic' parts can be passed in when making the request
    // Pass includeFullResponse in client options to return response along with headers
    makeRequest<T>(method: string, url: string, clientOptions: NvRequestOptions = {}): Observable<T> {
        return this.makeRequestEx<T>(method, url, clientOptions).pipe(
            map(response => {
                if (clientOptions?.includeFullResponse) {
                    return response as any;
                } else {
                    return response.body as T;
                }
            })
        );
    }

    get<T>(url: string, clientOptions: NvRequestOptions = {}): Observable<T> {
        return this.makeRequest('GET', url, clientOptions);
    }

    delete<T>(url: string, clientOptions: NvRequestOptions = {}): Observable<T> {
        return this.makeRequest('DELETE', url, clientOptions);
    }

    post<T>(url: string, clientOptions: NvRequestOptions = {}): Observable<T> {
        return this.makeRequest('POST', url, clientOptions);
    }

    patch<T>(url: string, clientOptions: NvRequestOptions = {}): Observable<T> {
        return this.makeRequest('PATCH', url, clientOptions);
    }

    put<T>(url: string, clientOptions: NvRequestOptions = {}): Observable<T> {
        return this.makeRequest('PUT', url, clientOptions);
    }

    // this is a variant of 'makeRequest' with the option to receive the full response
    makeRequestEx<T>(method: string, url: string, clientOptions: NvRequestOptions = {}): Observable<HttpResponse<T>> {
        const [options, formattedUrl] = this.initRequest(url, clientOptions);
        if (NvHttpEndpoint.isReportRequest(options)) {
            NvHttpEndpoint.httpStartEventsSubject.next({ method, url: formattedUrl, options });
        }
        options.reportRequestData['requestStartTime'] = Date.now().toString();
        const httpRequest = this.http.request<T>(method, formattedUrl, {
            body: options.body,
            params: options.params,
            headers: options.headers,
            // https://github.com/angular/angular/issues/18586
            responseType: options.responseType as 'json',
            observe: 'response',
            withCredentials: options.withCredentials ? options.withCredentials : false
        });
        return NvHttpEndpoint.applyTimeout(httpRequest, this.configOptions.config).pipe(
            retryWhen(errors => this.genericRetryStrategy(formattedUrl, options, errors)),
            map(response => {
                const result = extend(response, { config: { data: options.body } }) as HttpResponse<T>;
                options.reportRequestData['requestEndTime'] = Date.now().toString();
                if (
                    NvHttpEndpoint.isReportRequest(options) &&
                    options.reportRequestData['reportSuccessTelemetry'] !== 'false'
                ) {
                    NvHttpEndpoint.httpSuccessEventsSubject.next({ response: result, options });
                }
                return result;
            })
        );
    }

    getResponse<T>(url: string, clientOptions: NvRequestOptions = {}): Observable<HttpResponse<T>> {
        return this.makeRequestEx('GET', url, clientOptions);
    }
}
